import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getCategories,
  getCategory,
  getCategoryAttributesByCategoryId,
  createCategory,
  updateCategory,
  deleteCategory,
  reorderCategories,
  type Category,
  type CategoryAttribute,
  type CreateCategoryRequest,
  type ReorderCategoriesRequest,
} from "@/services/api/categories";
import { getCategoryLabel } from "@/features/categories/utils/categoryUtils";

const categoriesKeys = {
  all: ["categories"] as const,
  lists: () => [...categoriesKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...categoriesKeys.lists(), { filters }] as const,
  details: () => [...categoriesKeys.all, "detail"] as const,
  detail: (id: string | number) => [...categoriesKeys.details(), id] as const,
  attributes: (categoryId: string | number) =>
    [...categoriesKeys.detail(categoryId), "attributes"] as const,
};

export function useCategories(
  options?: Omit<UseQueryOptions<Category[], Error>, "queryKey" | "queryFn">,
) {
  return useQuery<Category[], Error>({
    queryKey: categoriesKeys.list(),
    queryFn: getCategories,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCategory(
  id: string | number,
  options?: Omit<UseQueryOptions<Category, Error>, "queryKey" | "queryFn">,
) {
  return useQuery<Category, Error>({
    queryKey: categoriesKeys.detail(id),
    queryFn: () => getCategory(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCategoryAttributes(
  categoryId: string | number,
  options?: Omit<
    UseQueryOptions<CategoryAttribute[], Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<CategoryAttribute[], Error>({
    queryKey: categoriesKeys.attributes(categoryId),
    queryFn: () => getCategoryAttributesByCategoryId(categoryId),
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation<Category, Error, CreateCategoryRequest>({
    mutationFn: createCategory,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: categoriesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["categories", "navigation"] });
      toast.success("Category created", {
        description: `"${getCategoryLabel(data)}" has been successfully created.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to create category", {
        description:
          error.message || "An error occurred while creating the category.",
      });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation<
    Category,
    Error,
    { id: string | number; data: CreateCategoryRequest }
  >({
    mutationFn: ({ id, data }) => updateCategory(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: categoriesKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: categoriesKeys.detail(data.id),
      });
      queryClient.invalidateQueries({ queryKey: ["categories", "navigation"] });
      toast.success("Category updated", {
        description: `"${getCategoryLabel(data)}" has been successfully updated.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to update category", {
        description:
          error.message || "An error occurred while updating the category.",
      });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string | number>({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoriesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["categories", "navigation"] });
      toast.success("Category deleted", {
        description: "The category has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast.error("Failed to delete category", {
        description:
          error.message || "An error occurred while deleting the category.",
      });
    },
  });
}

export function useReorderCategories() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, ReorderCategoriesRequest>({
    mutationFn: reorderCategories,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoriesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["categories", "navigation"] });
      toast.success("Categories reordered", {
        description: "The categories have been successfully reordered.",
      });
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: categoriesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["categories", "navigation"] });
      toast.error("Failed to reorder categories", {
        description:
          error.message || "An error occurred while reordering the categories.",
      });
    },
  });
}

export { categoriesKeys };
